import { Link } from 'react-router-dom'
import { useAuth, SignedIn, SignedOut, SignUpButton } from '@clerk/clerk-react'
import { Button, Tooltip, CreditsCounterSkeleton, TopBarActionsSkeleton } from '@/components/ui'
import { Plus, Coins, Menu } from 'lucide-react'
import { useUserStore } from '@/stores/user-store'

interface TopBarProps {
  onMenuClick: () => void
}

export function TopBar({ onMenuClick }: TopBarProps) {
  return (
    <header className="flex items-center justify-between px-4 md:px-6 py-2.5 glass border-t-0 border-x-0">
      <div className="flex items-center gap-3">
        <MenuButton onClick={onMenuClick} />
        <Logo />
      </div>
      <Actions />
    </header>
  )
}

function MenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="md:hidden p-2 -ml-2 rounded-lg text-[#888] hover:text-white hover:bg-white/[0.05] transition-colors"
      aria-label="Open menu"
    >
      <Menu className="size-5" />
    </button>
  )
}

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
      <img src="/pixelmaker_logo.svg" alt="Pixelmaker" className="size-5 brightness-0 invert" />
      <span className="text-lg font-bold tracking-tight text-white">
        Pixelmaker.ai
      </span>
    </Link>
  )
}

function Actions() {
  const { isLoaded: isClerkLoaded } = useAuth()
  const { credits, isLoading: isUserLoading } = useUserStore()
  
  // Show skeleton while Clerk is loading
  if (!isClerkLoaded) {
    return <TopBarActionsSkeleton />
  }
  
  return (
    <div className="flex items-center gap-3">
      <SignedOut>
        <SignUpButton mode="modal">
          <button className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 transition-colors">
            Sign Up Free
          </button>
        </SignUpButton>
      </SignedOut>
      <SignedIn>
        <CreditsCounter credits={credits} isLoading={isUserLoading} />
        <Link to="/video/i2v">
          <Button variant="glass-active" size="sm" className="md:px-3">
            <Plus className="size-3.5" />
            <span className="hidden md:inline">New Video</span>
          </Button>
        </Link>
      </SignedIn>
    </div>
  )
}

function CreditsCounter({ credits, isLoading }: { credits: number; isLoading: boolean }) {
  if (isLoading) {
    return <CreditsCounterSkeleton />
  }

  return (
    <Tooltip
      title="Credits Remaining"
      description={`You have ${credits.toLocaleString()} credits remaining`}
      showOnClick
    >
      <Link 
        to="/billing"
        className="flex items-center gap-2 px-3 py-1.5 rounded-md glass text-sm hover:bg-white/[0.08] transition-colors"
      >
        <Coins className="size-4 text-yellow-500" />
        <span className="font-medium text-white">
          {credits.toLocaleString()}
        </span>
      </Link>
    </Tooltip>
  )
}
